if (!customElements.get('fsk-product-page')) {
  customElements.define('fsk-product-page', class extends HTMLElement {
    connectedCallback() {
      if (this.controller && !this.controller.signal.aborted) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      if (this.querySelector('recipient-form')) {
        window.accessibilityStrings = window.accessibilityStrings || {};
        window.accessibilityStrings.recipientFormExpanded = this.dataset.recipientExpanded;
        window.accessibilityStrings.recipientFormCollapsed = this.dataset.recipientCollapsed;
      }
      this.baseAvailable = this.dataset.available === 'true';
      this.dialog = this.querySelector('dialog.fskdp__dialog');
      this.addEventListener('click', event => {
        const thumbnail = event.target.closest('[data-media-target]');
        if (thumbnail) this.selectMedia(thumbnail.dataset.mediaTarget);
        const link = event.target.closest('[data-zoom-open]');
        if (link) this.openZoom(event, link);
        if (event.target.closest('[data-zoom-close]')) this.dialog?.close();
      }, options);
      this.addEventListener('change', event => {
        if (event.target.matches('[data-variant-select]')) this.changeVariant(event.target.value);
      }, options);
      this.addEventListener('submit', event => {
        if (!event.target.closest('product-form')) return;
        const input = this.querySelector('.quantity__input');
        if (this.variantPending || !this.baseAvailable || input?.disabled || (input && !input.reportValidity())) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal: this.controller.signal });
      this.dialog?.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.dialog?.addEventListener('close', () => {
        document.body.style.overflow = this.previousOverflow || '';
        if (this.isConnected && this.zoomLink?.isConnected) this.zoomLink.focus();
      }, options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          if (this.variantPending) this.pendingCartRefresh = true;
          else this.refreshQuantity();
        });
      }
      this.selectMedia(this.dataset.mediaId || this.querySelector('[data-media-id].is-active')?.dataset.mediaId);
      this.classList.add('fskdp--enhanced');
      this.pendingCartRefresh = false;
      this.setVariantPending(false);
      this.setupReveals();
    }
    selectMedia(id) {
      const panels = [...this.querySelectorAll('.fskdp__media-column [data-media-id]')];
      if (!panels.some(panel => panel.dataset.mediaId === id)) return;
      for (const panel of panels) {
        const active = panel.dataset.mediaId === id;
        if (!active && panel.classList.contains('is-active')) {
          panel.querySelectorAll('video').forEach(video => video.pause());
          panel.querySelectorAll('iframe').forEach(frame => {
            if (frame.getAttribute('src')) frame.src = frame.src;
          });
          panel.querySelectorAll('model-viewer').forEach(model => model.pause?.());
        }
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
        panel.inert = !active;
      }
      this.querySelectorAll('[data-media-target]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.mediaTarget === id));
      });
      this.dataset.mediaId = id;
    }
    openZoom(event, link) {
      const image = this.dialog?.querySelector('[data-zoom-image]');
      if (!image || typeof this.dialog.showModal !== 'function') return;
      event.preventDefault();
      if (this.dialog.open) return;
      image.src = link.href;
      image.removeAttribute('srcset');
      image.alt = link.querySelector('img')?.alt || '';
      this.zoomLink = link;
      this.previousOverflow = document.body.style.overflow;
      this.dialog.showModal();
      document.body.style.overflow = 'hidden';
      this.dialog.querySelector('[data-zoom-close]')?.focus();
    }
    sectionUrl(variantId) {
      const url = new URL(location.href);
      url.hash = '';
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.dataset.section);
      url.searchParams.set('variant', variantId);
      return url;
    }
    async fetchSection(variantId, signal) {
      const response = await fetch(this.sectionUrl(variantId), { signal });
      if (!response.ok) throw new Error('Section unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = [...html.querySelectorAll('fsk-product-page')].find(element => element.dataset.section === this.dataset.section && element.dataset.productId === this.dataset.productId);
      if (!source || source.dataset.variantId !== variantId) throw new Error('Variant unavailable');
      return source;
    }
    setVariantPending(pending) {
      this.variantPending = pending;
      const panel = this.querySelector('[data-variant-panel]');
      if (panel) {
        panel.inert = pending;
        panel.setAttribute('aria-busy', String(pending));
      }
      this.syncQuantityState();
    }
    captureRecipient() {
      return [...this.querySelectorAll('recipient-form input[name], recipient-form textarea[name], recipient-form select[name]')]
        .filter(input => input.type !== 'hidden')
        .map(input => ({ name: input.name, type: input.type, value: input.value, checked: input.checked }));
    }
    restoreRecipient(values) {
      const fields = [...this.querySelectorAll('recipient-form input[name], recipient-form textarea[name], recipient-form select[name]')];
      for (const saved of values) {
        const field = fields.find(input => input.name === saved.name && input.type === saved.type);
        if (!field) continue;
        field.value = saved.value;
        if (field.type === 'checkbox' || field.type === 'radio') field.checked = saved.checked;
      }
      fields.filter(input => input.type === 'checkbox').forEach(input => input.dispatchEvent(new Event('change', { bubbles: true })));
    }
    async changeVariant(variantId) {
      this.variantRequest?.abort();
      this.quantityRequest?.abort();
      const select = this.querySelector('[data-variant-select]');
      const error = this.querySelector('[data-variant-error]');
      if (error) error.hidden = true;
      if (variantId === this.dataset.variantId) {
        this.setVariantPending(false);
        if (this.pendingCartRefresh) {
          this.pendingCartRefresh = false;
          this.refreshQuantity();
        }
        return;
      }
      if (!select || ![...select.options].some(option => option.value === variantId)) return;
      const request = new AbortController();
      this.variantRequest = request;
      this.setVariantPending(true);
      try {
        const source = await this.fetchSection(variantId, request.signal);
        if (request.signal.aborted || this.variantRequest !== request || !this.isConnected) return;
        const panel = this.querySelector('[data-variant-panel]');
        const replacement = source.querySelector('[data-variant-panel]');
        if (!panel || !replacement) throw new Error('Purchase panel unavailable');
        const quantity = this.querySelector('.quantity__input')?.value;
        const recipient = this.captureRecipient();
        panel.replaceWith(replacement);
        this.dataset.variantId = variantId;
        this.dataset.available = source.dataset.available;
        this.baseAvailable = source.dataset.available === 'true';
        const input = this.querySelector('.quantity__input');
        if (input && quantity) input.value = quantity;
        this.restoreRecipient(recipient);
        this.selectMedia(source.dataset.mediaId);
        select.value = variantId;
        const url = new URL(location.href);
        url.searchParams.set('variant', variantId);
        history.replaceState(history.state, '', url);
        window.Shopify?.PaymentButton?.init();
      } catch (errorResponse) {
        if (request.signal.aborted || this.variantRequest !== request || !this.isConnected) return;
        select.value = this.dataset.variantId;
        if (error) error.hidden = false;
      } finally {
        if (this.variantRequest === request && !request.signal.aborted && this.isConnected) {
          this.setVariantPending(false);
          if (this.pendingCartRefresh) {
            this.pendingCartRefresh = false;
            this.refreshQuantity();
          }
        }
      }
    }
    syncQuantityState() {
      const input = this.querySelector('.quantity__input');
      const button = this.querySelector('[name="add"]');
      const note = this.querySelector('[data-quantity-limit]');
      if (!input || !button) return;
      const min = Number(input.min) || 1;
      const step = Number(input.step) || 1;
      const max = input.hasAttribute('max') ? Number(input.max) : Infinity;
      const exhausted = max < min;
      const requested = Math.max(min, Math.min(Number(input.value) || min, max));
      input.disabled = exhausted || !this.baseAvailable;
      input.value = String(exhausted ? 0 : min + Math.floor((requested - min) / step) * step);
      this.querySelectorAll('.quantity__button').forEach(control => { control.disabled = input.disabled || this.variantPending; });
      button.disabled = !this.baseAvailable || exhausted || this.variantPending;
      if (note) note.hidden = !exhausted || !this.baseAvailable;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    async refreshQuantity() {
      this.quantityRequest?.abort();
      const request = new AbortController();
      this.quantityRequest = request;
      const variantId = this.dataset.variantId;
      try {
        const source = await this.fetchSection(variantId, request.signal);
        if (request.signal.aborted || this.quantityRequest !== request || this.variantPending || this.dataset.variantId !== variantId || !this.isConnected) return;
        const sourceInput = source.querySelector('.quantity__input');
        const input = this.querySelector('.quantity__input');
        if (!sourceInput || !input) return;
        for (const attribute of ['min', 'max', 'step', 'data-min', 'data-cart-quantity']) {
          if (sourceInput.hasAttribute(attribute)) input.setAttribute(attribute, sourceInput.getAttribute(attribute));
          else input.removeAttribute(attribute);
        }
        this.dataset.available = source.dataset.available;
        this.baseAvailable = source.dataset.available === 'true';
        this.syncQuantityState();
      } catch {}
    }
    setupReveals() {
      if (!('IntersectionObserver' in window) || !Element.prototype.animate || window.Shopify?.designMode) return;
      const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (preference.matches) return;
      const targets = [...this.querySelectorAll('.fskdp__detail-heading, .fskdp__accordions, .fskdp__section-head, .fskdp__related-card, .fskdp__help')];
      this.revealAnimations = new Map();
      const finish = target => {
        this.revealObserver?.unobserve(target);
        this.revealAnimations.get(target)?.cancel();
        this.revealAnimations.delete(target);
      };
      this.revealObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const target = entry.target;
          this.revealObserver.unobserve(target);
          if (preference.matches || target.contains(document.activeElement)) continue;
          const animation = target.animate([
            { opacity: 0, transform: 'translateY(8px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ], { duration: 900, easing: 'cubic-bezier(.22,.61,.36,1)' });
          this.revealAnimations.set(target, animation);
          animation.onfinish = () => this.revealAnimations.delete(target);
        }
      }, { rootMargin: '0px 0px 24px 0px', threshold: 0 });
      let fragmentTarget;
      try { fragmentTarget = document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch {}
      for (const target of targets) {
        if (target.getBoundingClientRect().top < window.innerHeight || fragmentTarget?.contains(target) || target.contains(fragmentTarget)) continue;
        this.revealObserver.observe(target);
      }
      const options = { signal: this.controller.signal };
      const finishInteraction = event => targets.filter(target => target.contains(event.target)).forEach(finish);
      this.addEventListener('focusin', finishInteraction, options);
      this.addEventListener('pointerdown', finishInteraction, options);
      this.querySelector('.fskdp__details-link')?.addEventListener('click', () => {
        targets.filter(target => target.closest('.fskdp__details')).forEach(finish);
      }, options);
      preference.addEventListener('change', event => {
        if (!event.matches) return;
        this.revealObserver.disconnect();
        targets.forEach(finish);
      }, options);
    }
    disconnectedCallback() {
      if (this.dialog?.open) {
        this.dialog.close();
        document.body.style.overflow = this.previousOverflow || '';
      }
      this.controller?.abort();
      this.variantRequest?.abort();
      this.quantityRequest?.abort();
      this.unsubscribe?.();
      this.revealObserver?.disconnect();
      this.revealAnimations?.forEach(animation => animation.cancel());
      this.revealAnimations?.clear();
    }
  });
}
